import React, { useEffect, useState, useContext } from 'react';
import MovielayoutHOC from '../layouts/Movie.layout';
import { useParams, Link } from 'react-router-dom';
import { MovieContext } from '../components/context/Movies.context';
import { FaPlay, FaTv, FaStar, FaArrowLeft } from 'react-icons/fa';
import PostSlider from '../components/PostSlider/PostSlider.component';
import MovieHero from '../components/MovieHero/MovieHero';
import tmdbService from '../services/tmdb';
import { MovieDetailSkeleton } from '../components/common/LoadingSkeleton';

const SeriesDetailPage = () => {
  const { id } = useParams();
  const { movie, setMovie, openWatch } = useContext(MovieContext);
  const [cast, setCast] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [episodeLoading, setEpisodeLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  // Fetch series details, cast & similar shows from TMDB
  useEffect(() => {
    let isMounted = true;
    const fetchSeriesData = async () => {
      setLoading(true);
      window.scrollTo(0, 0);
      try {
        const seriesData = await tmdbService.getTVDetails(id);
        const [creditsData, similarData] = await Promise.all([
          tmdbService.getTVCredits(id),
          tmdbService.getSimilarTV(id),
        ]);

        if (isMounted) {
          const normalized = seriesData
            ? {
                ...seriesData,
                title: seriesData.name || seriesData.original_name,
                original_title: seriesData.original_name || seriesData.name,
                media_type: 'tv',
              }
            : null;

          setMovie(normalized || null);
          setCast(creditsData?.cast ? creditsData.cast.slice(0, 10) : []);
          setSimilar(similarData || []);

          const seasonList = (seriesData?.seasons || [])
            .filter((s) => Number(s.season_number) > 0)
            .sort((a, b) => a.season_number - b.season_number);
          setSeasons(seasonList);
          setSelectedSeason((prev) =>
            prev ?? (seasonList.length > 0 ? seasonList[0].season_number : 1)
          );
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching TV series details:', err);
        if (isMounted) {
          setMovie(null);
          setLoading(false);
        }
      }
    };

    fetchSeriesData();
    return () => {
      isMounted = false;
    };
  }, [id, setMovie]);

  // Fetch episodes whenever the selected season changes
  useEffect(() => {
    let isMounted = true;
    const fetchEpisodes = async () => {
      if (!selectedSeason) return;
      setEpisodeLoading(true);
      try {
        const data = await tmdbService.getTVSeason(id, selectedSeason);
        if (isMounted) {
          setEpisodes(data?.episodes || []);
        }
      } catch (err) {
        console.error('Error fetching season episodes:', err);
        if (isMounted) setEpisodes([]);
      } finally {
        if (isMounted) setEpisodeLoading(false);
      }
    };

    fetchEpisodes();
    return () => {
      isMounted = false;
    };
  }, [id, selectedSeason]);

  if (loading) {
    return <MovieDetailSkeleton />;
  }

  const seriesItem = movie || {};

  // Dono actions (Play Now / Play on TV) ko wahi SxE jana hai, isliye options
  // ek hi jagah banao — TV par bhejne par path /watch/tv/<id>/<s>/<e> banta hai.
  const episodeWatchOptions = (ep) => ({
    mediaType: 'tv',
    season: selectedSeason,
    episode: ep.episode_number,
  });

  if (!seriesItem.id) {
    return (
      <div className="bg-dark-900 min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center">
        <div className="bg-dark-800/80 rounded-3xl border border-white/10 p-10 shadow-2xl">
          <div className="w-16 h-16 bg-accent-gold/10 text-accent-gold rounded-full flex items-center justify-center mx-auto mb-4 border border-accent-gold/20">
            <FaTv className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Nothing to show</h1>
          <p className="text-gray-400 text-sm max-w-md mx-auto mb-6">
            We couldn't find details for series ID{' '}
            <span className="text-accent-gold font-mono">"{id}"</span> in TMDB.
          </p>
          <Link
            to="/series"
            className="inline-flex items-center gap-2 bg-accent-gold hover:bg-accent-goldHover text-dark-900 font-extrabold text-xs px-6 py-3 rounded-xl transition-all shadow-lg"
          >
            <FaArrowLeft className="w-3.5 h-3.5" />
            <span>Return to Series Catalog</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dark-900 min-h-screen pb-16">
      {/* Series Hero Section */}
      <MovieHero />

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        {/* About the series Card */}
        <div className="bg-dark-800/80 rounded-2xl border border-white/10 p-6 md:p-8 mb-10 shadow-xl">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <span className="w-1.5 h-5 bg-accent-gold rounded-full inline-block" />
            About the Series
          </h2>
          <p className="text-gray-300 leading-relaxed text-sm md:text-base mb-6">
            {seriesItem.overview || 'No synopsis available for this title on TMDB.'}
          </p>

          {/* Quick Metrics from TMDB */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 border-t border-white/5 pt-6">
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wider block">Rating</span>
              <span className="text-sm font-bold text-accent-gold flex items-center gap-1 mt-1">
                <FaStar className="w-3.5 h-3.5" />
                {Number(seriesItem.vote_average || 0).toFixed(1)} / 10
              </span>
            </div>
            {seriesItem.first_air_date ? (
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider block">
                  First Aired
                </span>
                <span className="text-sm font-bold text-white mt-1 block">
                  {seriesItem.first_air_date}
                </span>
              </div>
            ) : null}
            {seriesItem.number_of_seasons ? (
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider block">Seasons</span>
                <span className="text-sm font-bold text-white mt-1 block">
                  {seriesItem.number_of_seasons}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Episodes Section */}
        {seasons.length > 0 && (
          <div className="bg-dark-800/80 rounded-2xl border border-white/10 p-6 md:p-8 mb-10 shadow-xl">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
                <span className="w-1.5 h-5 bg-accent-gold rounded-full inline-block" />
                Episodes
              </h2>

              {/* Season Selector */}
              <div className="flex items-center gap-2 bg-dark-700 px-3 py-2 rounded-xl border border-white/10 text-xs">
                <span className="text-gray-400 font-medium">Season</span>
                <select
                  value={selectedSeason || ''}
                  onChange={(e) => setSelectedSeason(Number(e.target.value))}
                  className="bg-transparent text-white font-semibold focus:outline-none cursor-pointer"
                >
                  {seasons.map((s) => (
                    <option key={s.season_number} value={s.season_number} className="bg-dark-800 text-white">
                      Season {s.season_number}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {episodeLoading ? (
              <div className="text-gray-400 text-sm py-6 text-center">
                Loading episodes...
              </div>
            ) : episodes.length === 0 ? (
              <div className="text-gray-400 text-sm py-6 text-center">
                No episodes listed for this season on TMDB.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
{episodes.map((ep) => (
                    <div
                      key={ep.id}
                      className="group bg-dark-700/70 hover:bg-dark-700 border border-white/5 hover:border-accent-gold/40 rounded-xl overflow-hidden text-left transition-all shadow-md hover:scale-[1.02]"
                    >
                      <div className="relative aspect-video w-full bg-black">
                        {ep.still_path ? (
                          <img
                            src={`https://image.tmdb.org/t/p/w500${ep.still_path}`}
                            alt={ep.name}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-600">
                            <FaTv className="w-8 h-8" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 to-transparent flex items-end p-3">
                          <span className="text-gray-300 text-[11px] font-bold">
                            S{String(selectedSeason).padStart(2, '0')}E
                            {String(ep.episode_number).padStart(2, '0')}
                            {ep.runtime ? ` · ${ep.runtime} min` : ''}
                          </span>
                        </div>
                      </div>
                      <div className="p-3">
                        <h4 className="text-white text-sm font-bold truncate">
                          {String(ep.episode_number).padStart(2, '0')} - {ep.name || 'Episode'}
                        </h4>
                        {ep.overview && (
                          <p className="text-gray-400 text-xs line-clamp-2 mt-1 leading-relaxed">
                            {ep.overview}
                          </p>
                        )}
                        {/* Phone par yahin, ya seedha TV par — dono ek hi card se */}
                        <div className="mt-3 flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => openWatch && openWatch(seriesItem, episodeWatchOptions(ep))}
                            className="w-full inline-flex items-center justify-center gap-2 bg-accent-gold hover:bg-accent-goldHover text-dark-900 text-xs font-extrabold px-3 py-2.5 rounded-lg transition-all shadow-md"
                          >
                            <FaPlay className="w-2.5 h-2.5" />
                            <span>Play Now</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => openWatch && openWatch(seriesItem, episodeWatchOptions(ep))}
                            className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-2.5 rounded-lg transition-all border border-white/10"
                          >
                            <FaTv className="w-3 h-3 text-accent-gold" />
                            <span>Play on TV</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Cast & Crew */}
        {cast && cast.length > 0 && (
          <div className="mb-12">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-1.5 h-5 bg-accent-gold rounded-full inline-block" />
              Cast & Crew
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4">
              {cast.map((actor) => (
                <div
                  key={actor.id}
                  className="bg-dark-800/60 rounded-2xl border border-white/5 p-3 text-center group hover:border-accent-gold/40 transition-all shadow-md"
                >
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden mb-3 border-2 border-white/10 group-hover:border-accent-gold transition-colors">
                    <img
                      src={
                        actor.profile_path
                          ? `https://image.tmdb.org/t/p/w200${actor.profile_path}`
                          : 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80'
                      }
                      alt={actor.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="text-white text-xs font-semibold truncate group-hover:text-accent-gold transition-colors">
                    {actor.name}
                  </h4>
                  <p className="text-gray-500 text-[11px] truncate mt-0.5">
                    {actor.character || 'Cast Member'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Similar Series */}
        {similar.length > 0 && (
          <section className="mb-12">
            <PostSlider
              title="More Like This"
              subtitle="Similar series from TMDB"
              posters={similar}
              isDark={true}
            />
          </section>
        )}
      </div>
    </div>
  );
};

export default MovielayoutHOC(SeriesDetailPage);